import { z } from "zod";
import { FabricTypeSchema, PillowShapeSchema } from "./enums.js";

// Editor configuration for a project. JSON-spec lives in ADR-0018; this Zod
// schema is the single source of truth, mirrored into `projects.config` via
// Drizzle's `.$type<ProjectConfig>()`. Snake-case property names match the
// JSONB shape the producer pipeline reads, so frontend / worker / api can all
// project the same record without renames.
export const PROJECT_CONFIG_PATTERNS = ["none", "dots", "stripes", "gradient"] as const;
export type ProjectConfigPattern = (typeof PROJECT_CONFIG_PATTERNS)[number];

export const PROJECT_CONFIG_PET_NAME_FONTS = ["serif", "sans", "script", "modern"] as const;
export type ProjectConfigPetNameFont = (typeof PROJECT_CONFIG_PET_NAME_FONTS)[number];

export const PROJECT_CONFIG_PET_NAME_POSITIONS = ["top", "bottom"] as const;
export type ProjectConfigPetNamePosition = (typeof PROJECT_CONFIG_PET_NAME_POSITIONS)[number];

export const PROJECT_CONFIG_SIZES = ["S", "M", "L"] as const;
export type ProjectConfigSize = (typeof PROJECT_CONFIG_SIZES)[number];

// The buyer's chosen printed face dimensions in whole cm (#228). Selected on the
// preview (silhouette slider / rectangle pick) and committed on "Zamów"; the
// checkout shows these exact numbers read-only, and the producer packet will
// print to them. `size` (S/M/L) stays the pricing tier; `size_cm` is the real
// footprint (silhouette is continuous, so the tier alone can't reconstruct it).
export const ProjectSizeCm = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type ProjectSizeCm = z.infer<typeof ProjectSizeCm>;

export const ProjectConfig = z.object({
  shape: PillowShapeSchema,
  size: z.enum(PROJECT_CONFIG_SIZES),
  // Null until the buyer commits a size on "Zamów" (#228). Optional so existing
  // JSONB rows written before this field parse cleanly.
  size_cm: ProjectSizeCm.nullish(),
  back_color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "back_color must be a 6-digit #rrggbb hex"),
  // Single-material MVP (#245): the one `default` fabric. Migration 0013 rewrites
  // any legacy stored value (e.g. "cotton") to "default". Strict validation is
  // safe because stored `config` is read as a typed JSONB column, not re-parsed
  // through this schema on the hot path — and the one runtime consumer
  // (`getVariantPricing`) resolves the fabric via `resolveVariant`, not
  // this field, so an unmigrated value can't break a read.
  material: FabricTypeSchema,
  pattern: z.enum(PROJECT_CONFIG_PATTERNS),
  pet_name: z.string().nullable(),
  pet_name_font: z.enum(PROJECT_CONFIG_PET_NAME_FONTS),
  pet_name_position: z.enum(PROJECT_CONFIG_PET_NAME_POSITIONS),
});
export type ProjectConfig = z.infer<typeof ProjectConfig>;

// Default config the api stamps onto every fresh `projects` row (ADR-0018 fn ⁵).
// Co-located with the schema so api inserts and the editor's "reset to defaults"
// path read from a single source of truth.
export const defaultProjectConfig = (): ProjectConfig => ({
  shape: "silhouette",
  size: "M",
  size_cm: null,
  back_color: "#ffffff",
  material: "default",
  pattern: "none",
  pet_name: null,
  pet_name_font: "sans",
  pet_name_position: "bottom",
});
